import { removeUndefined } from '../../../shared/utils/removeUndefined.ts';
import { Batch, NewOperation, Operation } from '../../types/sharedTypeImpl.ts';
import { reportError } from '../../utils/errorReporting.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { ModalDialog } from '../modalDialog.tsx';
import { OperationFormResult } from './operationDraft.ts';
import { OperationForm } from './operationForm.tsx';


interface Props {
    isOpen: boolean;
    batch?: Batch | null;
    otherOperations?: Operation[];
    operationUnderEdit?: Operation | null;
    onClose: () => void;
    onOperationSaved: (operation: Operation, newScrapLabel: number | undefined) => void;
}


export function OperationDialog({
    isOpen,
    batch,
    otherOperations,
    operationUnderEdit,
    onClose,
    onOperationSaved,
}: Props) {
    const operationCreate = trpc.operations.create.useMutation();
    const operationUpdate = trpc.operations.update.useMutation();

    const handleOperationSubmit = async (operationFormResult: OperationFormResult) => {
        const newOperation: NewOperation | Operation = {
            equipment: operationUnderEdit?.equipment ?? batch!.equipment,
            product: operationUnderEdit?.product ?? batch!.product,
            partDisplayName: operationUnderEdit?.partDisplayName ?? batch!.partDisplayName,
            ...(!operationUnderEdit && batch?.end && { effectiveTime: new Date(+batch.end - 1) }),
            ...operationFormResult,
            ...(operationUnderEdit && removeUndefined({
                _id: operationUnderEdit._id,
                effectiveTime: operationUnderEdit.effectiveTime,
                createdAt: operationUnderEdit.createdAt,
                partCount: operationUnderEdit.partCount,
                cycleTimeOverall: operationUnderEdit.cycleTimeOverall,
                cycleTimeRecent: operationUnderEdit.cycleTimeRecent,
                // The following fields should always be unset here, but we keep them in case logic changes.
                recordId: operationUnderEdit.recordId,
                scrapReview: operationUnderEdit.scrapReview,
                scrapCloseForm: operationUnderEdit.scrapCloseForm,
                scrapHandledAt: operationUnderEdit.scrapHandledAt,
            })),
        };

        const oldHasPotentialScraps = operationUnderEdit?.potentialScraps != null && operationUnderEdit.potentialScraps > 0;
        const newHasPotentialScraps = newOperation.potentialScraps != null && newOperation.potentialScraps > 0;
        const createScrapLabel = !oldHasPotentialScraps && newHasPotentialScraps;
        const releaseScrapLabel = oldHasPotentialScraps && !newHasPotentialScraps;

        try {
            const { operation, scrapLabel } = operationUnderEdit?._id == null
                ? await operationCreate.mutateAsync({ operation: newOperation as NewOperation, createScrapLabel })
                : await operationUpdate.mutateAsync({ operation: newOperation as Operation, createScrapLabel, releaseScrapLabel });
            onOperationSaved(operation, scrapLabel);
            onClose();
        } catch (error) {
            alert('Failed to save operation');
            reportError(error);
        }
    }


    if (!isOpen) return null;
    if (!batch && !operationUnderEdit) {
        reportError('Batch is required for new operations');
        return null;
    }

    return (
        <ModalDialog
            isOpen={isOpen}
            onCancel={onClose}
            frameClassName='modal-frame--operation-dialog'
            contentClassName='modal-content--tablet'
        >
            <OperationForm
                batch={batch ?? null}
                operationUnderEdit={operationUnderEdit}
                otherOperations={otherOperations}
                onOperationSubmit={handleOperationSubmit}
                isSaving={operationCreate.isPending || operationUpdate.isPending}
            />
        </ModalDialog>
    )
}
